import { useCombobox, useMultipleSelection } from "downshift";
import { ChevronDown, X } from "lucide-react";
import React, { forwardRef, useEffect } from "react";

import { Text } from "@/atoms";
import { cn } from "@/lib/utils";

export type MultiSelectOption<T = unknown> = {
  label: string;
  value: T;
  disabled?: boolean;
  supportingText?: string;
} & Partial<{ [key: string]: unknown }>;

type MultiSelectProps = {
  options: MultiSelectOption[];
  onSelect: (selected: MultiSelectOption[]) => void;
  placeholder?: string;
  value?: MultiSelectOption[];
  direction?: "up" | "down";
  "aria-invalid"?: boolean;
  "aria-describedby": string;
  id: string;
  disabled?: boolean;
};
const MultiSelect = forwardRef<HTMLDivElement, MultiSelectProps>(
  ({ options, onSelect, placeholder, disabled = false, ...props }, ref) => {
    const [inputValue, setInputValue] = React.useState("");

    const [selectedItems, setSelectedItems] = React.useState<
      MultiSelectOption[]
    >(
      props.value
        ? Array.isArray(props.value)
          ? props.value
          : [props.value]
        : []
    );

    useEffect(() => {
      setSelectedItems(props.value || []);
    }, [props.value]);

    const items = React.useMemo(
      () => getFilteredItems(options, selectedItems, inputValue),
      [options, selectedItems, inputValue]
    );

    const { getDropdownProps, removeSelectedItem } = useMultipleSelection({
      selectedItems,
      onStateChange({ selectedItems: newSelectedItems, type }) {
        switch (type) {
          case useMultipleSelection.stateChangeTypes
            .SelectedItemKeyDownBackspace:
          case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownDelete:
          case useMultipleSelection.stateChangeTypes.DropdownKeyDownBackspace:
          case useMultipleSelection.stateChangeTypes.FunctionRemoveSelectedItem:
            setSelectedItems(newSelectedItems as MultiSelectOption[]);
            onSelect(newSelectedItems as MultiSelectOption[]);
            break;
          default:
            break;
        }
      },
    });
    const {
      isOpen,
      getToggleButtonProps,
      getMenuProps,
      getInputProps,
      highlightedIndex,
      getItemProps,
      selectedItem,
    } = useCombobox({
      items,
      itemToString: (item) => item?.label || "",
      defaultHighlightedIndex: 0,
      selectedItem: null,
      inputValue,
      stateReducer: (state, actionAndChanges) => {
        const { changes, type } = actionAndChanges;

        switch (type) {
          case useCombobox.stateChangeTypes.InputKeyDownEnter:
          case useCombobox.stateChangeTypes.ItemClick:
            return {
              ...changes,
              isOpen: true,
              highlightedIndex: 0,
            };
          default:
            return changes;
        }
      },
      onStateChange({
        inputValue: newInputValue,
        type,
        selectedItem: newSelectedItem,
      }) {
        switch (type) {
          case useCombobox.stateChangeTypes.InputKeyDownEnter:
          case useCombobox.stateChangeTypes.ItemClick:
          case useCombobox.stateChangeTypes.InputBlur:
            if (newSelectedItem && !newSelectedItem.disabled) {
              setSelectedItems([...selectedItems, newSelectedItem]);
              onSelect([...selectedItems, newSelectedItem]);
              setInputValue("");
            }
            break;

          case useCombobox.stateChangeTypes.InputChange:
            setInputValue(newInputValue || "");

            break;
          default:
            break;
        }
      },
    });

    const renderSelections = () =>
      !!selectedItems.length && (
        <div
          className={cn(
            "selections relative flex flex-wrap gap-1 border-b border-slate-100 px-2 py-2 pr-8",
            disabled && "cursor-not-allowed opacity-50"
          )}
        >
          {selectedItems.map(function renderSelectedItem(
            selectedItemForRender,
            index
          ) {
            return (
              <button
                className="focus:bg-light-blue flex cursor-pointer items-center rounded-full border border-gray-600 bg-white px-3 py-1 text-black focus:outline-none focus:ring-1 focus:!ring-blue-400"
                key={`selected-item-${index}`}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeSelectedItem(selectedItemForRender);
                }}
              >
                <Text
                  component="p"
                  text={selectedItemForRender.label}
                  variant="body3"
                />
                <X height={16} width={16} />
              </button>
            );
          })}

          <button
            className="absolute right-2 top-1/2 -translate-y-1/2 text-black"
            type="button"
            onClick={() => setSelectedItems([])}
          >
            <X height={16} width={16} />
          </button>
        </div>
      );

    const renderInput = () => (
      <div className="relative flex grow gap-0.5">
        <input
          className={cn(
            "peer block h-10 w-full flex-1 appearance-none rounded",
            "bg-white text-sm text-gray-800 transition-colors ease-in-out",
            "border-none",
            props["aria-invalid"] && "placeholder:text-red",
            disabled && "cursor-not-allowed opacity-50"
          )}
          disabled={disabled}
          placeholder={placeholder}
          {...getInputProps(getDropdownProps({ preventKeyAction: isOpen }))}
        />
        <button
          aria-label="toggle menu"
          className="absolute right-0 top-1/2 -translate-y-1/2 px-2"
          type="button"
          {...getToggleButtonProps()}
        >
          <ChevronDown
            aria-invalid={props["aria-invalid"]}
            aria-label={isOpen ? "Close menu" : "Open menu"}
            className={cn(
              isOpen ? "rotate-180" : "rotate-0",
              "origin-center transition-transform",
              props["aria-invalid"] ? "red" : ""
            )}
            data-testid="dropdown-toggle-arrow"
          />
        </button>
      </div>
    );

    const renderOptions = () => (
      <ul
        className={`absolute z-10 mt-1 max-h-80 w-full overflow-scroll rounded bg-white p-0 shadow-md ${
          !(isOpen && items.length) && "hidden"
        }`}
        {...getMenuProps()}
      >
        {isOpen &&
          items.map((item, index) => (
            <li
              className={cn(
                highlightedIndex === index && "bg-blue-300",
                selectedItem === item && "font-bold",
                "flex flex-col px-3 py-2 shadow-sm",
                item.disabled &&
                  "pointer-events-none cursor-not-allowed opacity-50"
              )}
              key={`${item.value}${index}`}
              {...getItemProps({ item, index, disabled: item.disabled })}
            >
              <span>{item.label}</span>
              {item.supportingText && (
                <p className="text-xs text-gray-500">{item.supportingText}</p>
              )}
            </li>
          ))}
      </ul>
    );

    return (
      <div
        className={cn("MultiSelect rounded-xs relative w-full")}
        data-testid="MultiSelect"
        ref={ref}
      >
        <div className="flex flex-col gap-1">
          <div
            className={cn(
              "inline-flex flex-wrap  items-center gap-2 rounded bg-white shadow-sm",
              "peer block h-10 w-full flex-1 appearance-none rounded",
              "-ring-offset-1 peer block w-full appearance-none rounded border-none text-primary ring-1 ring-light-grey placeholder:!text-transparent focus:outline-none focus:ring-1 focus:!ring-blue-400 focus:placeholder:!text-secondary aria-invalid:ring-1 aria-invalid:ring-red aria-invalid:placeholder:text-red",
              props["aria-invalid"] && "ring-1 ring-red placeholder:text-red"
            )}
          >
            {renderSelections()}

            {renderInput()}
          </div>
        </div>
        {renderOptions()}
      </div>
    );
  }
);

MultiSelect.displayName = "MultiSelect ";

export default MultiSelect;

function getFilteredItems(
  options: MultiSelectOption[],
  selectedItems: MultiSelectOption[],
  inputValue: string
): MultiSelectOption[] {
  const lowerCasedInputValue = inputValue.toLowerCase();
  return options.filter((opt) => {
    return (
      !selectedItems.some((selected) => selected.value === opt.value) &&
      opt.label.toLowerCase().includes(lowerCasedInputValue)
    );
  });
}

/**
 * Returns a list of MultiSelectOptions whose value matches the provided values
 * @param values - The values to match
 * @param options - The options to match
 * @returns A list of options that match the values
 */
export function getMultiSelectOptions<T>(
  values: T[],
  options: MultiSelectOption<T>[]
): MultiSelectOption<T>[] {
  return options.filter((option) => values.includes(option.value));
}
